import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';

import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';

const EditSpaceCard = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const formRef = useRef(null);

    const [form, setForm] = useState({ 
        title: '', 
        description: ''
    });

    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchSpaceCard = async () => {
            try {
                const res = await api.get(`/space-cards/${slug}`);
                if (res.status === 200) {
                    const spaceCard = res.data.spaceCard;
                    setForm({
                        title: spaceCard.title || '',
                        description: spaceCard.description || ''
                    });

                    const imageUrl = spaceCard.image?.startsWith('http')
                        ? spaceCard.image
                        : `${res.data.base_url}${spaceCard.image}`;

                    setImagePreview(spaceCard.image ? imageUrl : null);
                }
            } catch (err) {
                handleErrors(err);
            }
        };

        fetchSpaceCard();

        $.validator.addMethod("ckeditorRequired", function (value, element) {
            const textOnly = value.replace(/<[^>]*>/g, '').trim();
            return textOnly.length > 0;
        }, "This field is required");

        if (formRef.current && $.fn.validate) {
            $(formRef.current).validate({
                ignore: ".ck-editor *",
                rules: {
                    title: {
                        required: true,
                        minlength: 3
                    },
                    description: {
                        required: true,
                        minlength: 3,
                        ckeditorRequired: true
                    }
                },
                messages: {
                    title: {
                        required: "Please enter a title",
                        minlength: "Title must be at least 3 characters"
                    },
                    description: {
                        required: "Please enter description",
                        minlength: "Description must be at least 3 characters"
                    }
                },
                errorElement: "label",
                validClass: "is-valid",
                errorClass: "is-invalid text-danger",
                highlight: function (element, errorClass, validClass) {
                    $(element).addClass(errorClass).removeClass(validClass);
                },
                unhighlight: function (element, errorClass, validClass) {
                    $(element).removeClass(errorClass).addClass(validClass);
                }
            });
        }


    }, [slug]);

    const handleFormSubmit = async () => {
        const $ = window.jQuery;
        if (!$(formRef.current).valid()) return;

        if (!$(formRef.current).valid()) return;

        const formData = new FormData();
        Object.entries(form).forEach(([k, v]) => formData.append(k, v));
        
        formData.append("image", image);

        setImage(null);
        setImagePreview(null);

        try {
            setSubmitting(true);
            const res = await api.put(
                `/space-cards/${slug}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                }
            );
            if (res.status === 200) {
                showToast("success", res.data.message, "Success");
                navigate(ROUTES.SPACECARDS);
            }
        } catch (err) {
            handleErrors(err);
        }
        finally {
            setSubmitting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.SPACECARDS}>Space Cards</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to="#/">Edit Space Card</Link>
                    </li>
                </ol>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">Edit Space Card</h4>
                        </div>
                        <div className="card-body">
                            <form ref={formRef} noValidate>
                                <div className="row">

                                    {[
                                        { name: "title", label: "Title" }
                                    ].map(({ label, name }) => (
                                        <div key={name} className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                            <label htmlFor={name}>{label}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name={name}
                                                id={name}
                                                value={form[name]}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    ))}

                                    {[
                                        { name: 'description', label: 'Description' }
                                    ].map(({ name, label }) => (
                                        <div key={name} className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                            <div className="mb-3 col-lg-12">
                                                <label className="form-label">{label}</label>
                                                <CKEditor
                                                    editor={ClassicEditor}
                                                    config={{
                                                        toolbar: [
                                                            'heading', '|',
                                                            'bold', 'italic', 'link', 'code', 'codeBlock', 
                                                            '|', 'bulletedList', 'numberedList'
                                                        ]
                                                    }}
                                                    data={form[name]}
                                                    onChange={(event, editor) => {
                                                        const data = editor.getData();
                                                        setForm(prev => ({ ...prev, [name]: data }));
                                                        document.querySelector(`input[name="${name}"]`).value = data;
                                                    }}
                                                />
                                                <input
                                                    type="hidden"
                                                    name={name}
                                                    value={form[name]}
                                                />
                                            </div>
                                        </div>
                                    ))}

                                    <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                        <label htmlFor="image" className="form-label">Image</label>
                                        <div className="input-group custom_file_input">
                                            <div className="form-file">
                                                <input
                                                    name="image"
                                                    type="file"
                                                    className="form-file-input form-control"
                                                    onChange={handleImageChange}
                                                />
                                            </div>
                                            <span className="input-group-text">Upload</span>
                                        </div>
                                        {imagePreview && (
                                            <div className="mt-2">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    style={{ width: 100, height: 100, objectFit: 'cover' }}
                                                />
                                            </div>
                                        )}
                                    </div>

                                </div>

                                <div className="pt-3">
                                    <button
                                        type="button"
                                        onClick={handleFormSubmit}
                                        className="btn btn-sm btn-primary me-2"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <i className="spinner-border spinner-border-sm me-1"></i>
                                                Submit
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-floppy-disk me-1"></i>
                                                Submit
                                            </>
                                        )}
                                    </button>
                                    <Link to={ROUTES.SPACECARDS} className="btn btn-sm btn-danger light">
                                        <i className="fas fa-times-circle"></i>&nbsp;
                                        Cancel
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default EditSpaceCard
